import React, { useContext, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, ButtonGroup, TextStyle } from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import {
  EventEmitterWithIndex,
  getTranslatedText,
  usePersonalisedState
} from '@nile/components/src/Utils/helpers';
import { getColumns } from './ProjectListColumns';
import { getMonths } from '@nile/banyan/src/Constants/datetime';
import {
  getFieldOptions,
  getType,
  getAccessor,
  isFilterable,
  isSortable,
  getStatusTranslationKeys
} from '@nile/projects/src/Pebbles/ProjectListTableContainer';
import {
  ALL_TABLES,
  COMMON_CONSTANTS,
  useSuperListHook,
  translateText,
  getAllColumnWidth,
  getColumnWidth,
  saveColumnWidth,
  useProduct,
  AppContext,
  getDefaultValue,
  getDateOperators,
  Loader,
  getDefaultRowCount
} from '@nile/components';
import {
  getDynamicQuery,
  checkDynamicQuery,
  generateOptions,
  fetchValues,
  getVariables,
  getAsyncOptions
} from '@nile/projects/src/Pebbles/ProjectListTableContainer';
import {
  AvatarCell,
  LinkCell
} from '@nile/client_management/src/Components/Cells';
import { TextCell } from '@nile/mangifera/src/Components/Ticket';
import DateTimeCell from '@nile/mangifera/src/Components/Task/DateTimeCell';
import NumberCell from '@nile/projects/src/Components/Cells/NumberCell';
import BadgeCell from '@nile/projects/src/Components/Cells/BadgeCell';
import GroupHeaderCell from '@nile/projects/src/Components/Cells/GroupHeaderCell';
import TaskCell from '@nile/projects/src/Components/Cells/TaskCell';
import MilestoneCell from '@nile/projects/src/Components/Cells/MilestoneCell';
import SuperList from '@nile/components/src/Components/SuperList/SuperList';
import { TicketClient } from '@nile/graphql';
import { GET_PROJECTS } from '@nile/projects/src/Graphql';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';

const defaultColumnCells = {
  Link: LinkCell,
  Avatar: AvatarCell,
  DateTime: DateTimeCell,
  Badge: BadgeCell,
  Text: TextCell,
  Number: NumberCell,
  Task: TaskCell,
  Milestone: MilestoneCell
};

const TableContainer = styled.div`
  height: ${(props) =>
    props.fitContentHeight
      ? 'auto'
      : props.adjustForWidgets
      ? 'calc(100% - 155px)'
      : props.adjustForGrouping
      ? 'calc(100% - 51px)'
      : 'calc(100%)'};
  .table {
    min-width: 400px !important;
  }

  .columnHeader {
    flex: none !important;
  }

  .table-body td {
    // height: auto !important;
    vertical-align: top !important;
  }

  .tbody {
    display: inline-block;
    overflow: auto;

    .tr {
      min-height: 42px;
    }

    padding-top: 12px;
  }

  .tr .td,
  .inblock {
    padding-left: 4px;
  }

  .unFreezeContent {
    width: 99%;
    overflow: auto;
    white-space: nowrap;
    position: relative;
    // padding-left: 20px;
  }
  .unFreezeContent:hover {
    overflow: auto;
  }
  .cell {
    font-weight: ${(props) => (props.notBold ? '400 !important' : '')};
  }
`;

const ProjectListTableContainer = (props) => {
  const intl = useIntl();
  const isITProduct = useProduct(COMMON_CONSTANTS.IT);
  const ticketContext = useContext(TicketContext);
  const { accountType } = useContext(AppContext);
  const tabContext = useContext(TabContext);

  const { projectViewMeta, projectViews } = props;
  const { match } = props;
  const { id, sectionId } = match.params;
  const viewId = sectionId || id;
  const tableName = ALL_TABLES.projectTable;
  const rowCount = getDefaultRowCount(tableName);
  const columnWidthObject = getAllColumnWidth(tableName);
  const isGroupingEnabled = true;

  const GROUPING_TABS = [
    {
      id: 'DUE_DATE',
      content: translateText('attribute.dueDate'),
      accessibilityLabel: 'DUE_DATE',
      panelID: 'DUE_DATE',
      groupBySortParam: [
        {
          attribute: 'dueDate',
          order: 'ASC'
        }
      ]
    },
    {
      id: 'STATUS',
      content: translateText('attribute.status'),
      accessibilityLabel: 'STATUS',
      panelID: 'STATUS',
      groupBySortParam: [
        {
          attribute: 'status',
          order: 'ASC'
        }
      ]
    },
    {
      id: 'CLIENTS',
      content: translateText('admin.sla.tabs.clients'),
      accessibilityLabel: 'CLIENTS',
      panelID: 'CLIENTS',
      groupBySortParam: [
        {
          attribute: 'client.name',
          order: 'ASC'
        }
      ]
    },
    {
      id: 'PROJECT_OWNER',
      content: translateText('project.view.owner'),
      accessibilityLabel: 'PROJECT_OWNER',
      panelID: 'PROJECT_OWNER',
      groupBySortParam: [
        {
          attribute: 'owner.name',
          order: 'ASC'
        }
      ]
    },
    {
      id: 'OVERDUE',
      content:translateText('attribute.overdue'),
      accessibilityLabel: 'OVERDUE',
      panelID: 'OVERDUE',
      groupBySortParam: [
        {
          attribute: 'dueDate',
          order: 'ASC'
        }
      ]
    }
  ];

  const isItProduct = useProduct(COMMON_CONSTANTS.IT);

  const PROJECT_GROUPING_TABS = isItProduct ? GROUPING_TABS.filter((tab) => tab.id !== 'CLIENTS') : GROUPING_TABS;

  const [viewLoading, setViewLoading] = useState(true);
  const [columnGroup, setColumnGroup] = useState(null);
  const [data, setData] = useState([]);

  const [selectedTab, setSelectedTab, loading] = usePersonalisedState(
    'DUE_DATE',
    {
      name: 'project-group-tabs',
      category: viewId
    }
  );

  useEffect(() => {
    if(isItProduct && selectedTab === 'CLIENTS') {
      setSelectedTab('DUE_DATE');
    }
  }, [isItProduct, selectedTab]);

  function getGroupBySortParams(tabId) {
    const groupBySortParams = PROJECT_GROUPING_TABS?.find(
      (tab) => tab.id === tabId
    )?.groupBySortParam;

    return groupBySortParams;
  }

  const SECONDARY_SORT = getGroupBySortParams(selectedTab);

  const DEFAULT_CONDITION = useMemo(() => {
    if(selectedTab === 'OVERDUE') {
      return {
        joinOperator: 'AND',
        operands: [
          { attribute: 'overdue', operator: 'is', value: true }
        ]
      };
    }
    return null;
  }, [selectedTab]);

  const getGroupAccessor = (rowItem) => {
    switch (selectedTab) {
      case 'DUE_DATE': {
        
         // Handle missing due date
         if (!rowItem?.dueDate) {
          return {
            id: 'no-due-date',
            name: rowItem?.dueDate,
            empty: intl.formatMessage({
              id: 'client.details.tktSummary.unassigned'
            })
          };
        }

        // Process due date
        const dueDate = new Date(rowItem.dueDate);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        const dueMonth = dueDate.getMonth();
        const dueYear = dueDate.getFullYear();

        // Determine date category
        let id, label, year;
        if (dueMonth === currentMonth && dueYear === currentYear) {
          id = 'This Month';
          label = 'placeholder.this.month';
        } else if (
          dueMonth === (currentMonth + 1) % 12 &&
          dueYear === (currentMonth === 11 ? currentYear + 1 : currentYear)
        ) {
          id = 'Next Month';
          label = 'placeholder.next.month';
        } else {
          const dueMonthText = getMonths()[dueMonth];
          id = `${dueMonthText?.label}-${dueYear}`;
          label =
            'admin.holidayManagement.month.' +
            dueMonthText?.label.toLowerCase();
          year = dueYear;
        }

        return {
          id,
          name: (
            <GroupHeaderCell
              svgIconType="default"
              iconName="TimerNav"
              iconColor="#333333"
              labelText={label}
              subLabel={year}
            />
          )
        };
      }
      case 'STATUS':
        return {
          id: rowItem?.status,
          name:
            rowItem?.status === 'Blocked' ? (
              <GroupHeaderCell
                svgIconType="default"
                iconName="WarningIcon"
                iconColor="#ED7777"
                labelText="project.task.status.blocked"
                textcolor="var(--sop-red-color)"
              />
            ) : (
              <GroupHeaderCell
                svgIconType="withColorBg"
                iconName="ProjectLogo"
                iconColor="#333333"
                bgColor="#E3EDFF"
                labelText={getStatusTranslationKeys()[rowItem?.status]}
              />
            )
        };
      case 'CLIENTS':
        return {
          id: rowItem?.client?.name,
          name: (
            <GroupHeaderCell
              svgIconType="withColorBg"
              iconName="ClientLogo"
              iconColor="#333333"
              bgColor="#F2F2F2"
              labelText={rowItem?.client?.name}
            />
          )
        };
      case 'PROJECT_OWNER':
        return {
          id: rowItem?.owner?.name,
          name: (
            <GroupHeaderCell
              svgIconType="avatar"
              labelText={rowItem?.owner?.name}
            />
          )
        };
        case 'OVERDUE':
          return{
            id: 'overdue',
            name: (
              <GroupHeaderCell
                svgIconType="default"
                iconName="WarningIcon"
                iconColor="#ED7777"
                labelText="attribute.overdue"
                textcolor="var(--sop-red-color)"
              />
            )
          }
      default:
        return {
          id: rowItem?.id,
          name: ''
        };
    }
  };

  const { columns: cols, loading: columnLoading } = getColumns({
    viewId: viewId,
    module: 'project'
  });

  const getActions = (col) => {
    switch (col.name) {
      case 'title':
        return {
          onClick: (item) => {
            if (item.row.original) {
              ticketContext.openTab({
                module: 'PROJECT_DETAIL',
                data: item.row.original
              });
            }
          }
        };
      default:
        return null;
    }
  };

  async function generateColumns() {
    setViewLoading(true);
    if (cols.length === 0) {
      setViewLoading(false);
      return;
    }

    const columns = cols
      .filter((col) => col.selected)
      .map((col) => {
        return {
          Header: getTranslatedText(intl, col?.label),
          header: getTranslatedText(intl, col?.label),
          accessor: getAccessor(col),
          accessorKey: getAccessor(col),
          size: getColumnWidth(columnWidthObject, col?.name),
          minWidth: col.name === 'subject' ? 400 : 50,
          type: getType(col),
          id: col?.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: !isSortable(col, projectViewMeta),
          disableFilters: !isFilterable(col, projectViewMeta),
          editable: false,
          dataType: col?.dataType,
          filterType: col?.filterType,
          isEditable: false,
          helpers: {
            entity: 'Project',
            getAsyncOptions,
            getDefaultValue,
            getDynamicQuery,
            checkDynamicQuery,
            generateOptions,
            getDateOperators,
            fetchValues,
            getVariables,
            updateConditions: updateTableFilterCondition
          },
          actions: getActions(col),
          field: col,
          fieldOptions: getFieldOptions(col),
          customTextStyles: col.name === 'dueDate' ? true : false
        };
      });
    setColumnGroup(columns);
    setViewLoading(false);
  }

  const {
    records,
    setRecords,
    tableCondition,
    updateTableFilterCondition,
    advanceFilterCondition,
    updateListInfoProperties
  } = useSuperListHook({
    tableName: ALL_TABLES.projectTable,
    viewId: viewId,
    showOnlyOnAdvancedFilter: false,
    showOnlyOnTableFilter: false
  });

  useEffect(() => {
    if (!columnLoading) {
      generateColumns();
    }
  }, [columnLoading, cols]);

  useEffect(() => {
    const view = projectViews.find((pview) => pview.id === viewId);
    tabContext.setActiveView(view);
  }, [viewId]);

  useEffect(() => {
    EventEmitterWithIndex.subscribe('refreshProjectColumns', generateColumns);
    return function cleanup() {
      EventEmitterWithIndex.unsubscribe('refreshProjectColumns');
    };
  }, []);

  function tabSelectHandler(tabId) {
    setSelectedTab(tabId);
    setRecords([]);
    // Whenever the internal tab is changed we need to reset page and sort
    updateListInfoProperties({ page: 1, sort: {} });
  }

  if (columnLoading && viewLoading && loading) {
    return (
      <div className="fhp w100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="fhp">
      {isGroupingEnabled && (
        <span className="pl-40 pt-4 pb-4 fx center g20">
          <TextStyle disabled>
            <FormattedMessage id="project.groupby.title" />
          </TextStyle>

          <ButtonGroup>
            {PROJECT_GROUPING_TABS?.map(({ content, id: tabId }) => {
              return (
                <Button
                  onClick={() => tabSelectHandler(tabId)}
                  outline={selectedTab === tabId}
                  plain={selectedTab !== tabId}
                  monochrome
                >
                  <FormattedMessage id={content} />
                </Button>
              );
            })}
          </ButtonGroup>
        </span>
      )}
      <TableContainer key={selectedTab} adjustForGrouping={isGroupingEnabled}>
        <SuperList
          // Table Configuration
          tableName={tableName} // Table config (get row count, save sorting, save column widths personalization)
          viewId={viewId} // Triggers data fetch view change & save sorting personalization with the viewID, listinfo from store
          idKey="projectId" // Unique identifier for each row
          columns={columnGroup} // constructing columns, pass columns to table to display headers and map data to the columns
          defaultColumnCells={defaultColumnCells} // Data cell components mapping to the column from getType function
          // Data & Query Configuration
          client={TicketClient} // client to use for the query
          query={GET_PROJECTS} // query to use for the table
          data={data} // data from the query response for the table to display data in the columns and other actions on row data
          setData={setData} // function to set data from the query response for the table
          accessorKey="getProjects" // key to access the data from the query response
          dataKey="projects" // second key to access the data from the query response
          secondarySort={SECONDARY_SORT} // values to pass to the query to get the table data with secondary sort i.e group by sort
          additionalVariables={{
            isItProduct: isITProduct
          }} // additional variables to pass to the query to get the table data
          appendViewIdToListInfo // appends view id to the query to get the table data
          // Selection & Interaction
          records={records} // selected records from the table with useSuperListHook (store)
          setRecords={setRecords} // function to set selected records from the table with useSuperListHook (store)
          noBulkSelect={accountType !== 'MSP'} // Disable bulk select for non-MSP users(Client Portal)
          checkBoxSize="lg" // Size of selection checkboxes
          padded="lg" // Padding style
          alignStart // Alignment configuration
          // Loading & Empty Project List
          skeletonLoading={viewLoading} // show loading until columns are constructed
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/ProjectEmptyState.svg`,
            heading: getTranslatedText(intl, 'alert.noProjectFound.heading'),
            description: getTranslatedText(intl, 'alert.noProjectFound.desc')
          }} // show this image, heading, description when projects is empty
          // Grouping & Display
          maintainSelection={true} // Maintain selection across page changes
          isGroupingEnabled={isGroupingEnabled} // enables row grouping
          defaultExpanded // expands all rows by default
          groupAccessor={getGroupAccessor} // function to get the group accessor header component
          defaultCondition={DEFAULT_CONDITION}
          noOverscan={true} // To ensure all rows are rendered
          overScanCount={rowCount*2} // overscan count to ensure all rows are rendered
          // Column Management
          columnWidthPersonalization // enables column width personalization
          saveColumnWidth={saveColumnWidth} // function to save column width personalization
          // Filtering
          tableCondition={tableCondition} // inline table filter conditions in useSuperListHook (store)
          advanceFilterCondition={advanceFilterCondition} // advanced filter conditions (header filters) in useSuperListHook (store)
        />
      </TableContainer>
    </div>
  );
};

export default withRouter(ProjectListTableContainer);
